/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, useState, type MouseEvent, type ReactNode } from 'react';
import { CustomerPortalContext, CustomerPortalAction, type CustomerPortalAccount } from '../../../contexts/CustomerPortalContext';
import {
	Box,
	Button,
	ClickAwayListener,
	Divider,
	List,
	ListItem,
	ListItemButton,
	Popper,
	Typography,
	Tooltip
} from '@mui/material';
import { IconArrowDown, IconArrowUp, IconCustomerPortal, IconDocumentation, IconSignOut } from '../../../assets';
import { useDimensions } from '../../../hooks/useDimensions/useDimensions';

const docsUrl = 'https://ckeditor.com/docs/ckeditor5/latest/index.html';

const userNameStyles = { textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 'var(--user-name-max-width)' };
const userNameButtonStyles = { color: 'text.primary', fontWeight: '500', fontSize: '14px', whiteSpace: 'nowrap' };
const dropdownStyles = { borderRadius: '4px', backgroundColor: 'background.paper', boxShadow: '0px 2px 8px 0px #00000026' };
const dropdownItemStyles = { gap: '10px', padding: '12px 20px' };
const popperStyles = { zIndex: '1300' };

export function AccountInfoDropdown(): ReactNode {
	const { performCustomerPortalAction, customerPortalAccount, getCustomerPortalUrl } = useContext( CustomerPortalContext );
	const [ dropdownElement, setDropdownElement ] = useState<HTMLElement | null>( null );

	const [ userNameRef, setUserNameRef ] = useState<HTMLSpanElement | null>( null );
	const { hasOverflowX: hasUserNameOverflowX } = useDimensions( userNameRef );

	const isOpen = Boolean( dropdownElement );
	const id = isOpen ? 'account-info-dropdown' : undefined;

	function handleButtonClick( event: MouseEvent<HTMLElement> ) {
		setDropdownElement( dropdownElement ? null : event.currentTarget );
	}

	function handleLogoutClick() {
		performCustomerPortalAction( CustomerPortalAction.LOG_OUT );
	}

	return (
		<ClickAwayListener onClickAway={ () => setDropdownElement( null ) }>
			<Box>
				<Button
					color={ 'secondary' }
					sx={ userNameButtonStyles }
					aria-describedby={ id }
					onClick={ handleButtonClick }
					endIcon={ isOpen ? <img src={ IconArrowUp }/> : <img src={ IconArrowDown }/> }
				>
					<Tooltip disableHoverListener={ !hasUserNameOverflowX } title={ getUserLabel( customerPortalAccount ) }>
						<span ref={ setUserNameRef } style={ userNameStyles }>
							{ getUserLabel( customerPortalAccount ) }
						</span>
					</Tooltip>
				</Button>
				<Popper id={ id } open={ isOpen } anchorEl={ dropdownElement } sx={ popperStyles }>
					<List sx={ dropdownStyles } data-testid={ 'account-info-dropdown-list' }>
						<ListItem disablePadding>
							<ListItemButton
								sx={ dropdownItemStyles }
								component="a"
								href={ getCustomerPortalUrl() }
								target="_blank"
							>
								<img src={ IconCustomerPortal }/><Typography fontWeight={ 400 } fontSize={ 12 }>Customer Portal</Typography>
							</ListItemButton>
						</ListItem>
						<Divider/>
						<ListItem disablePadding>
							<ListItemButton sx={ dropdownItemStyles } component="a" href={ docsUrl } target="_blank">
								<img src={ IconDocumentation }/><Typography fontWeight={ 400 } fontSize={ 12 }>Documentation</Typography>
							</ListItemButton>
						</ListItem>
						<Divider/>
						<ListItem disablePadding>
							<ListItemButton sx={ dropdownItemStyles } onClick={ handleLogoutClick }>
								<img src={ IconSignOut }/><Typography fontWeight={ 400 } fontSize={ 12 }>Sign out</Typography>
							</ListItemButton>
						</ListItem>
					</List>
				</Popper>
			</Box>
		</ClickAwayListener>
	);
}

function getUserLabel( customerPortalAccount: CustomerPortalAccount | null ): string {
	const firstName = customerPortalAccount?.userDetails?.firstName;
	const lastName = customerPortalAccount?.userDetails?.lastName;
	const email = customerPortalAccount?.userDetails?.email;

	if ( firstName && lastName ) {
		return `${ firstName } ${ lastName }`;
	}

	if ( email ) {
		return email;
	}

	return '';
}
